import { queryClient } from 'src/query-client'
import { groupsService } from '../service/groups-service'
import type {
  AdminGroupMembersListResponse,
  AdminGroupPostsListResponse,
  AdminGroupsListResponse,
  CreateGroupPayload,
  CreateGroupPostInput,
  DiscoverGroupsParams,
  Group,
  GroupMember,
  GroupMemberResponse,
  GroupPost,
  GroupPostsListResponse,
  UpdateGroupPayload,
} from '../types/groups'
import { queryKeys } from '../util/query-keys'
import {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query'

export const useDiscoverGroups = (
  params?: DiscoverGroupsParams,
): UseQueryResult<{ data: (Group & { isJoined: boolean })[]; meta: { limit: number; offset: number; total: number } }, Error> => {
  return useQuery(queryKeys.discoverGroups(params?.search), () => groupsService.discoverGroups(params), {
    keepPreviousData: true,
  })
}

export const useMyGroups = (): UseQueryResult<Group[], Error> => {
  return useQuery(queryKeys.myGroups(), () => groupsService.myGroups(), {
    keepPreviousData: true,
  })
}

export const useGroup = (groupId: string): UseQueryResult<Group, Error> => {
  return useQuery(queryKeys.group(groupId), () => groupsService.getGroupById(groupId), {
    enabled: !!groupId,
    keepPreviousData: true,
  })
}

export const useGroupMembers = (
  groupId: string,
  params?: { limit?: number; offset?: number },
): UseQueryResult<GroupMember[], Error> => {
  return useQuery(queryKeys.groupMembers(groupId), () => groupsService.listMembers(groupId, params), {
    enabled: !!groupId,
    keepPreviousData: true,
  })
}

export const useGroupPosts = (
  groupId: string,
  params?: { limit?: number },
): UseInfiniteQueryResult<GroupPostsListResponse, Error> => {
  const limit = params?.limit ?? 20
  return useInfiniteQuery<GroupPostsListResponse, Error>({
    queryKey: queryKeys.groupPosts(groupId),
    queryFn: ({ pageParam = 0 }) => groupsService.getGroupPosts(groupId, { limit, offset: pageParam as number }),
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit
      if (nextOffset >= lastPage.total) return undefined
      return nextOffset
    },
    enabled: !!groupId,
  }) as UseInfiniteQueryResult<GroupPostsListResponse, Error>
}

export const useJoinGroupByCode = (): UseMutationResult<GroupMemberResponse, Error, string> => {
  return useMutation<GroupMemberResponse, Error, string>((inviteCode) => groupsService.joinGroupByCode(inviteCode), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(queryKeys.myGroups())
      queryClient.invalidateQueries(queryKeys.discoverGroups())
      if (data.groupId) {
        queryClient.invalidateQueries(queryKeys.group(data.groupId))
      }
    },
  })
}

export const useJoinPublicGroup = (): UseMutationResult<GroupMemberResponse, Error, string> => {
  return useMutation<GroupMemberResponse, Error, string>((groupId) => groupsService.joinPublicGroup(groupId), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.myGroups())
      queryClient.invalidateQueries(queryKeys.discoverGroups())
    },
  })
}

export const useLeaveGroup = (): UseMutationResult<void, Error, string> => {
  return useMutation<void, Error, string>((groupId) => groupsService.leaveGroup(groupId), {
    onSuccess: (_data, groupId) => {
      queryClient.invalidateQueries(queryKeys.myGroups())
      queryClient.invalidateQueries(queryKeys.group(groupId))
    },
  })
}

export const useRegenerateInviteCode = (): UseMutationResult<Group, Error, string> => {
  return useMutation<Group, Error, string>((groupId) => groupsService.regenerateInviteCode(groupId), {
    onSuccess: (_data, groupId) => {
      queryClient.invalidateQueries(queryKeys.group(groupId))
    },
  })
}

export const useCreateGroupPost = (): UseMutationResult<GroupPost, Error, { groupId: string; input: CreateGroupPostInput }> => {
  return useMutation<GroupPost, Error, { groupId: string; input: CreateGroupPostInput }>(
    ({ groupId, input }) => groupsService.createGroupPost(groupId, input),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(queryKeys.groupPosts(variables.groupId))
      },
    },
  )
}

export const useDeleteGroupPost = (): UseMutationResult<void, Error, { groupId: string; postId: string }> => {
  return useMutation<void, Error, { groupId: string; postId: string }>(
    ({ groupId, postId }) => groupsService.deleteGroupPost(groupId, postId),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(queryKeys.groupPosts(variables.groupId))
      },
    },
  )
}

export const useTogglePinPost = (): UseMutationResult<void, Error, { groupId: string; postId: string }> => {
  return useMutation<void, Error, { groupId: string; postId: string }>(
    ({ groupId, postId }) => groupsService.togglePinPost(groupId, postId),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(queryKeys.groupPosts(variables.groupId))
      },
    },
  )
}

// Admin hooks
export const useAdminGroups = (params: {
  limit: number
  offset: number
  search?: string
}): UseQueryResult<AdminGroupsListResponse, Error> => {
  return useQuery(queryKeys.adminGroups(params.offset, params.search), () => groupsService.adminListGroups(params), {
    keepPreviousData: true,
    retry: (failureCount, error) => ((error as any)?.response?.status === 403 ? false : failureCount < 3),
  })
}

export const useAdminGroup = (groupId: string): UseQueryResult<Group, Error> => {
  return useQuery(queryKeys.adminGroup(groupId), () => groupsService.adminGetGroup(groupId), {
    enabled: !!groupId,
    retry: (failureCount, error) => ((error as any)?.response?.status === 403 ? false : failureCount < 3),
  })
}

export const useAdminGroupMembers = (
  groupId: string,
  params: { limit: number; offset: number },
): UseQueryResult<AdminGroupMembersListResponse, Error> => {
  return useQuery(queryKeys.adminGroupMembers(groupId, params.offset), () => groupsService.adminListMembers(groupId, params), {
    enabled: !!groupId,
    keepPreviousData: true,
  })
}

export const useAdminGroupPosts = (
  groupId: string,
  params: { limit: number; offset: number },
): UseQueryResult<AdminGroupPostsListResponse, Error> => {
  return useQuery(queryKeys.adminGroupPosts(groupId, params.offset), () => groupsService.adminListPosts(groupId, params), {
    enabled: !!groupId,
    keepPreviousData: true,
  })
}

export const useCreateAdminGroup = (): UseMutationResult<Group, Error, CreateGroupPayload> => {
  return useMutation<Group, Error, CreateGroupPayload>((input) => groupsService.adminCreateGroup(input), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.adminGroups())
    },
  })
}

export const useUpdateAdminGroup = (): UseMutationResult<Group, Error, { groupId: string; input: UpdateGroupPayload }> => {
  return useMutation<Group, Error, { groupId: string; input: UpdateGroupPayload }>(
    ({ groupId, input }) => groupsService.adminUpdateGroup(groupId, input),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(queryKeys.adminGroups())
        queryClient.invalidateQueries(queryKeys.adminGroup(variables.groupId))
      },
    },
  )
}

export const useDeleteAdminGroup = (): UseMutationResult<void, Error, string> => {
  return useMutation<void, Error, string>((groupId) => groupsService.adminDeleteGroup(groupId), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.adminGroups())
    },
  })
}

export const useAddAdminGroupMembers = (): UseMutationResult<void, Error, { groupId: string; hustlerIds: string[] }> => {
  return useMutation<void, Error, { groupId: string; hustlerIds: string[] }>(({ groupId, hustlerIds }) =>
    groupsService.adminAddMembers(groupId, hustlerIds),
  )
}

export const useRemoveAdminGroupMember = (): UseMutationResult<void, Error, { groupId: string; memberId: string }> => {
  return useMutation<void, Error, { groupId: string; memberId: string }>(({ groupId, memberId }) =>
    groupsService.adminRemoveMember(groupId, memberId),
  )
}
